import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, ReferenceLine, Legend } from "recharts";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

const fmt = (d) => {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString(undefined, { day: "2-digit", month: "short" }); } catch { return d; }
};

function Sparkline({ data, keys, colors, refLines, unit, testid }) {
  if (!data || data.length === 0) return <div className="text-xs text-slate-400">No data</div>;
  return (
    <div className="h-32 w-full" style={{ minHeight: 128, minWidth: 200 }} data-testid={testid}>
      <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={128}>
        <LineChart data={data.map((d) => ({ ...d, _d: fmt(d.date) }))} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
          <XAxis dataKey="_d" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} width={36} />
          <Tooltip contentStyle={{ fontSize: 11 }} formatter={(v, k) => [`${v}${unit ? " " + unit : ""}`, k.toUpperCase()]} />
          {refLines?.map((r, i) => <ReferenceLine key={i} y={r.y} stroke={r.color} strokeDasharray="3 3" />)}
          {keys.map((k, i) => (
            <Line key={k} type="monotone" dataKey={k} stroke={colors[i]} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function DeltaBadge({ series, key1, invert }) {
  if (!series || series.length < 2) return null;
  const first = series[0][key1];
  const last = series[series.length - 1][key1];
  if (first == null || last == null) return null;
  const diff = last - first;
  const pct = first ? ((diff / first) * 100).toFixed(1) : "0";
  const good = invert ? diff < 0 : diff > 0;
  const Icon = Math.abs(diff) < 0.01 ? Minus : (diff > 0 ? TrendingUp : TrendingDown);
  const cls = Math.abs(diff) < 0.01
    ? "bg-slate-100 text-slate-600"
    : good
    ? "bg-emerald-100 text-emerald-700"
    : "bg-red-100 text-red-700";
  return (
    <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${cls}`}>
      <Icon size={10} /> {diff > 0 ? "+" : ""}{diff.toFixed(1)} ({pct}%)
    </div>
  );
}

export default function TrendCharts({ patientId }) {
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/patients/${patientId}/trends`);
        setTrends(r.data.trends);
      } catch (e) { console.debug("[silent]", e); }
      setLoading(false);
    })();
  }, [patientId]);

  if (loading) return <div className="text-xs text-slate-500">Loading trends…</div>;
  if (!trends) return null;

  const has = (k) => (trends[k] || []).length > 0;
  const anyData = Object.values(trends).some((arr) => arr && arr.length > 0);

  if (!anyData) {
    return <div className="text-xs text-slate-500">No historical data to trend yet. Save a visit with vitals or investigations to see charts here.</div>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {has("bp") && (
        <Card title="Blood Pressure" unit="mmHg" delta={<DeltaBadge series={trends.bp} key1="sbp" invert />}>
          <Sparkline
            data={trends.bp}
            keys={["sbp", "dbp"]}
            colors={["#dc2626", "#4f46e5"]}
            refLines={[{ y: 140, color: "#f59e0b" }, { y: 90, color: "#f59e0b" }]}
            unit="mmHg"
            testid="trend-bp"
          />
        </Card>
      )}
      {has("bmi") && (
        <Card title="BMI" delta={<DeltaBadge series={trends.bmi} key1="bmi" invert />}>
          <Sparkline data={trends.bmi} keys={["bmi"]} colors={["#059669"]} refLines={[{ y: 25, color: "#f59e0b" }]} testid="trend-bmi" />
        </Card>
      )}
      {has("hba1c") && (
        <Card title="HbA1c" unit="%" delta={<DeltaBadge series={trends.hba1c} key1="value" invert />}>
          <Sparkline data={trends.hba1c} keys={["value"]} colors={["#7c3aed"]} refLines={[{ y: 7, color: "#f59e0b" }, { y: 6.5, color: "#10b981" }]} unit="%" testid="trend-hba1c" />
        </Card>
      )}
      {has("sugar") && (
        <Card title="Blood Sugar">
          <Sparkline data={trends.sugar} keys={["fbs", "ppbs", "rbs"]} colors={["#0ea5e9", "#f97316", "#ef4444"]} unit="mg/dL" testid="trend-sugar" />
        </Card>
      )}
      {has("ldl") && (
        <Card title="LDL Cholesterol" unit="mg/dL" delta={<DeltaBadge series={trends.ldl} key1="value" invert />}>
          <Sparkline data={trends.ldl} keys={["value"]} colors={["#f43f5e"]} refLines={[{ y: 100, color: "#f59e0b" }]} testid="trend-ldl" />
        </Card>
      )}
      {has("creatinine") && (
        <Card title="Serum Creatinine" unit="mg/dL" delta={<DeltaBadge series={trends.creatinine} key1="value" invert />}>
          <Sparkline data={trends.creatinine} keys={["value"]} colors={["#0891b2"]} refLines={[{ y: 1.2, color: "#f59e0b" }]} testid="trend-creatinine" />
        </Card>
      )}
    </div>
  );
}

function Card({ title, unit, delta, children }) {
  return (
    <div className="border border-slate-200 rounded-md p-3 bg-white">
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-semibold text-slate-700">{title} {unit && <span className="text-slate-400 font-normal">({unit})</span>}</div>
        {delta}
      </div>
      {children}
    </div>
  );
}
